import { Link } from 'react-router-dom'
import { editionReadUrl, editionSourceUrl, pageImageUrl, type PageBrief } from '../../lib/api'
import { useSession } from '../../lib/session'
import { Glyph } from './Glyph'
import { pageLabel } from './model'

/**
 * The edition rail: the issue this page belongs to, and where the reader's
 * query landed in it.
 *
 * A hit count per page is the whole point of the rail. A researcher with a
 * query open is navigating the edition by where the matches are, not by page
 * number, and the count is what makes that possible without opening each page.
 */

export function EditionRail({
  pages,
  currentPageId,
  editionId,
  pageCount,
  hitsByPage,
  jumpPageId,
}: {
  pages: PageBrief[]
  currentPageId: string
  editionId: string | null
  pageCount: number | null
  hitsByPage: Map<string, number>
  jumpPageId: string | null
}) {
  const { can } = useSession()
  return (
    <aside
      className="rail flex w-[176px] flex-none flex-col"
      aria-label="Pages in this edition"
    >
      <div className="flex flex-none items-baseline justify-between border-b border-n300 px-3 pb-2 pt-2.5">
        <div className="chrome">Edition</div>
        <div className="font-mono text-[10px] text-ink3">{pageCount ? `${pageCount} PP` : '-'}</div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-2.5 py-2">
        {pages.map((page) => {
          const current = page.id === currentPageId
          const hits = hitsByPage.get(page.id) ?? 0
          const ratio = page.width_px && page.height_px ? page.width_px / page.height_px : 400 / 818
          return (
            <Link
              key={page.id}
              to={`/page/${page.id}${window.location.search}`}
              aria-current={current ? 'page' : undefined}
              className="flex items-stretch gap-2 rounded-[--radius-md] p-1 no-underline"
              style={{
                background: current ? 'var(--color-bg)' : 'transparent',
                boxShadow: current ? 'inset 0 0 0 1px var(--jl-accent)' : 'none',
              }}
            >
              <div
                className="flex-none overflow-hidden bg-well"
                style={{ width: 34, aspectRatio: String(ratio) }}
              >
                {page.withdrawn ? (
                  <div
                    className="h-full w-full"
                    style={{
                      background:
                        'repeating-linear-gradient(135deg,#3a3735 0 3px,#252321 3px 6px)',
                    }}
                  />
                ) : (
                  <img
                    src={pageImageUrl(page.id, true)}
                    alt=""
                    loading="lazy"
                    className="block h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-[3px]">
                <div
                  className={`font-mono text-[11.5px] ${current ? 'text-ink' : 'text-ink2'}`}
                >
                  {pageLabel(page.section_label, page.page_number)}
                </div>
                {page.withdrawn ? (
                  <div className="font-mono text-[9.5px] uppercase text-ink3">withdrawn</div>
                ) : hits ? (
                  <div className="flex items-center gap-1">
                    <span className="h-1 w-1 rounded-full bg-accent" />
                    <span className="font-mono text-[9.5px] text-ink2">
                      {hits} hit{hits > 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <div className="font-mono text-[9.5px] text-ink4">-</div>
                )}
                {page.id === jumpPageId && (
                  <div className="flex items-center gap-1 font-mono text-[9px] text-art">
                    JUMP
                    <Glyph name="jump" size={9} strokeWidth={1.8} />
                  </div>
                )}
              </div>
            </Link>
          )
        })}
      </div>

      <div className="flex flex-none flex-col gap-1.5 border-t border-n300 px-3 py-2.5">
        <Link to={editionId ? `/browse/${editionId}` : '/browse'} className="text-[13px]">
          Issue contents
        </Link>
        <Link to="/browse" className="text-[13px]">
          Browse by date
        </Link>
        {editionId && can('content.view_page_image') && (
          <a href={editionReadUrl(editionId)} className="text-[13px]" target="_blank" rel="noopener">
            Read the whole issue (PDF)
          </a>
        )}
        {editionId && can('content.download') && (
          <a href={editionSourceUrl(editionId)} className="text-[13px]" download>
            Download PDF
          </a>
        )}
      </div>
    </aside>
  )
}
